import { useCallback, useEffect, useMemo, useReducer } from 'react';

import { deleteKnowledgeCard, listKnowledgeTags } from '@/api/knowledge/api';
import { useKnowledgeCardContext } from '@/hooks/knowledge-card.store';
import {
  buildAssetLibraryQuery,
  buildAssetTagOptions,
  formatAssetCount,
  getAssetOpenTarget,
  getAssetShareUrl,
  type AssetLibraryFilters,
  type AssetLibraryTagOption,
} from '@/models/asset-library.model';
import { type KnowledgeCard } from '@/models/knowledge-card.model';

interface AssetLibraryControlState {
  filters: AssetLibraryFilters;
  tagOptions: AssetLibraryTagOption[];
}

enum AssetLibraryControlActionType {
  QUERY_CHANGED = 'QUERY_CHANGED',
  SOURCE_TYPE_CHANGED = 'SOURCE_TYPE_CHANGED',
  TAG_CHANGED = 'TAG_CHANGED',
  TAG_OPTIONS_LOADED = 'TAG_OPTIONS_LOADED',
}

type AssetLibraryControlAction =
  | { type: AssetLibraryControlActionType.QUERY_CHANGED; payload: string | null }
  | { type: AssetLibraryControlActionType.SOURCE_TYPE_CHANGED; payload: string | null }
  | { type: AssetLibraryControlActionType.TAG_CHANGED; payload: string | null }
  | {
      type: AssetLibraryControlActionType.TAG_OPTIONS_LOADED;
      payload: AssetLibraryTagOption[];
    };

const initialControlState: AssetLibraryControlState = {
  filters: { query: null, sourceType: null, tag: null },
  tagOptions: [],
};

const controlReducer = (
  state: AssetLibraryControlState,
  action: AssetLibraryControlAction,
): AssetLibraryControlState => {
  switch (action.type) {
    case AssetLibraryControlActionType.QUERY_CHANGED:
      return { ...state, filters: { ...state.filters, query: action.payload } };
    case AssetLibraryControlActionType.SOURCE_TYPE_CHANGED:
      return { ...state, filters: { ...state.filters, sourceType: action.payload } };
    case AssetLibraryControlActionType.TAG_CHANGED:
      return { ...state, filters: { ...state.filters, tag: action.payload } };
    case AssetLibraryControlActionType.TAG_OPTIONS_LOADED:
      return { ...state, tagOptions: action.payload };
    default:
      return state;
  }
};

/** Options for `useAssetLibrary`. */
export interface UseAssetLibraryOptions {
  clientId: string;
  projectId?: string;
  accessToken?: string | null;
  onOpenAsset?: (cardId: string) => void;
}

/** Full return type of `useAssetLibrary`. */
export interface UseAssetLibraryResult {
  cards: KnowledgeCard[];
  total: number;
  totalLabel: string;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  filters: AssetLibraryFilters;
  tagOptions: AssetLibraryTagOption[];
  setFilterQuery: (value: string | null) => void;
  setFilterSourceType: (value: string | null) => void;
  setFilterTag: (value: string | null) => void;
  openCard: (card: KnowledgeCard) => void;
  viewCard: (card: KnowledgeCard) => void;
  openOriginal: (card: KnowledgeCard) => void;
  copyLink: (card: KnowledgeCard) => Promise<boolean>;
  deleteCard: (card: KnowledgeCard) => Promise<void>;
  changePage: (page: number, pageSize: number) => void;
  retry: () => void;
}

/**
 * Orchestrates the asset library view: filter state, paginated card fetching, tag option loading,
 * and card actions (open, view, copy link, delete).
 * Automatically re-fetches whenever filters or pagination change.
 *
 * @param options.onOpenAsset - Callback invoked when the user opens a note card in the editor.
 * @returns Reactive card list, filter controls, pagination, and card action handlers.
 */
export const useAssetLibrary = ({
  clientId,
  projectId,
  accessToken,
  onOpenAsset,
}: UseAssetLibraryOptions): UseAssetLibraryResult => {
  const { cards, total, page, pageSize, loading, error, fetchCards, clearError } =
    useKnowledgeCardContext();
  const [controlState, dispatch] = useReducer(controlReducer, initialControlState);

  const loadCards = useCallback(
    (nextPage: number, nextPageSize: number): void => {
      if (!accessToken || !clientId) {
        return;
      }
      const query = buildAssetLibraryQuery(projectId, controlState.filters, nextPage, nextPageSize);
      void fetchCards(accessToken, clientId, query);
    },
    [accessToken, clientId, controlState.filters, fetchCards, projectId],
  );

  useEffect(() => {
    loadCards(1, pageSize);
  }, [loadCards, pageSize]);

  useEffect(() => {
    if (!accessToken || !clientId) {
      dispatch({ type: AssetLibraryControlActionType.TAG_OPTIONS_LOADED, payload: [] });
      return undefined;
    }

    let cancelled = false;
    void listKnowledgeTags(accessToken, clientId, projectId ?? null)
      .then((response) => {
        if (!cancelled) {
          dispatch({
            type: AssetLibraryControlActionType.TAG_OPTIONS_LOADED,
            payload: buildAssetTagOptions(response.items),
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          dispatch({ type: AssetLibraryControlActionType.TAG_OPTIONS_LOADED, payload: [] });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, clientId, projectId]);

  const setFilterQuery = useCallback((value: string | null): void => {
    dispatch({ type: AssetLibraryControlActionType.QUERY_CHANGED, payload: value });
  }, []);
  const setFilterSourceType = useCallback((value: string | null): void => {
    dispatch({ type: AssetLibraryControlActionType.SOURCE_TYPE_CHANGED, payload: value });
  }, []);
  const setFilterTag = useCallback((value: string | null): void => {
    dispatch({ type: AssetLibraryControlActionType.TAG_CHANGED, payload: value });
  }, []);

  const openCard = useCallback(
    (card: KnowledgeCard): void => {
      const target = getAssetOpenTarget(card);
      if (target.kind === 'editor') {
        onOpenAsset?.(target.cardId);
      } else if (target.kind === 'external') {
        globalThis.open(target.url, '_blank', 'noopener,noreferrer');
      }
    },
    [onOpenAsset],
  );

  const openOriginal = useCallback((card: KnowledgeCard): void => {
    if (card.sourceUrl) {
      globalThis.open(card.sourceUrl, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const viewCard = useCallback(
    (card: KnowledgeCard): void => {
      if (card.sourceType === 'document' && card.sourceUrl) {
        openOriginal(card);
      } else {
        openCard(card);
      }
    },
    [openCard, openOriginal],
  );

  const copyLink = useCallback(
    async (card: KnowledgeCard): Promise<boolean> => {
      const url = getAssetShareUrl(card, globalThis.location.origin, clientId, projectId);
      try {
        await navigator.clipboard.writeText(url);
        return true;
      } catch {
        return false;
      }
    },
    [clientId, projectId],
  );

  const deleteCard = useCallback(
    async (card: KnowledgeCard): Promise<void> => {
      if (!accessToken || !clientId) {
        throw new Error('Unable to delete asset: missing client context');
      }
      await deleteKnowledgeCard(accessToken, clientId, card.cardId);
      loadCards(1, pageSize);
    },
    [accessToken, clientId, loadCards, pageSize],
  );

  const changePage = useCallback(
    (nextPage: number, nextPageSize: number): void => loadCards(nextPage, nextPageSize),
    [loadCards],
  );

  const retry = useCallback((): void => {
    clearError();
    loadCards(page, pageSize);
  }, [clearError, loadCards, page, pageSize]);

  return useMemo(
    () => ({
      cards,
      total,
      totalLabel: formatAssetCount(total),
      page,
      pageSize,
      loading,
      error,
      filters: controlState.filters,
      tagOptions: controlState.tagOptions,
      setFilterQuery,
      setFilterSourceType,
      setFilterTag,
      openCard,
      viewCard,
      openOriginal,
      copyLink,
      deleteCard,
      changePage,
      retry,
    }),
    [
      cards,
      changePage,
      controlState,
      copyLink,
      deleteCard,
      error,
      loading,
      openCard,
      openOriginal,
      page,
      pageSize,
      retry,
      setFilterQuery,
      setFilterSourceType,
      setFilterTag,
      total,
      viewCard,
    ],
  );
};
